import { Button, FormControlLabel, Checkbox, Grid, IconButton, TextField, Tooltip } from "@material-ui/core";
import { orderBy } from "lodash";
import { arrayMove, sortableContainer, sortableElement, sortableHandle } from 'react-sortable-hoc';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import HighlightOffOutlinedIcon from '@material-ui/icons/HighlightOffOutlined';
import { StepsToCompleteStyles } from "./style";
import { useGetAllStepsByWorkOrderQuery, useUpdateActivityTypeStepMutation } from "features/activity/ActivityTypeStepSlice";
import { useEffect, useState } from "react";
import NotificationProvider from "components/NotificationProvider";
import { useSelector } from "react-redux";
import { selectIsClient, selectIsVendor } from "features/user/userSlice";
import { activityPermissionProfile } from "components/Authorize/permissionProfiles/activityPermissionProfile";
import { isVendorEditLocked } from "features/activity/activityConsts";
import Authorize from "components/Authorize";

const StepsToComplete = ({ activity, setHasUnsavedChanges }) => {

    const classes = StepsToCompleteStyles();

    let isClient = useSelector(selectIsClient);
    const isVendor = useSelector(selectIsVendor);
    const vendorLocked = isVendorEditLocked(activity, isVendor);

    let { data: steps, refetch: refetchSteps } = useGetAllStepsByWorkOrderQuery(activity?.workOrderId, { skip: !activity?.workOrderId });
    let [updateSteps, { isLoading: updatingSteps }] = useUpdateActivityTypeStepMutation();

    const [itemList, setItemList] = useState([]);
    const [overwriteConfig, setOverwriteConfig] = useState(false);

    useEffect(() => {
        if (steps) {
            setItemList(steps.map(a => ({ ...a, id: a.id.toString() })));
        }
    }, [steps]);

    const values = orderBy([...itemList], 'sequenceNumber');

    const onAddNewStep = () => {
        const newStep = {
            description: '',
            isCompleted: false,
            entityId: activity?.id,
            entityType: 1,
            activityGroupId: activity?.groupId,
            WorkOrderId: activity?.workOrderId,
            sequenceNumber: itemList.length,
            id: null,
        };
        setItemList([...itemList, newStep]);
        setHasUnsavedChanges(true);
    };

    const onValueChange = (newVal, index) => {
        let valuesToSave = [...itemList];
        valuesToSave[index] = { ...valuesToSave[index], description: newVal };
        changeItemList(valuesToSave);
    };

    const onSelectStep = (index, val) => {
        let valuesToSave = [...itemList];
        valuesToSave[index] = { ...valuesToSave[index], isCompleted: val };
        changeItemList(valuesToSave);
    };

    const changeItemList = (valuesToSave) => {
        setItemList(valuesToSave.map((v, index) => ({ ...v, sequenceNumber: index })));
        setHasUnsavedChanges(true);
    };

    const onDeleteValue = (rowIndex) => {
        let valuesToSave = [...itemList];
        valuesToSave.splice(rowIndex, 1);
        setItemList(valuesToSave.map((v, index) => ({ ...v, sequenceNumber: index })));
        setHasUnsavedChanges(true);
    };

    const onSortEnd = ({ oldIndex, newIndex }) => {
        setItemList(arrayMove([...itemList], oldIndex, newIndex).map((q, index) => ({ ...q, sequenceNumber: index })));
    };

    const onSaveAction = async () => {
        let arrayCopy = itemList
            .filter(v => v.description)
            .map(element => ({ ...element, id: element.id ? element.id : 0 }));
        let request = {
            ActivityTypeStepsEntities: arrayCopy,
            ActivityGroupId: activity?.groupId,
            WorkOrderId: activity?.workOrderId,
            OverwriteConfig: overwriteConfig,
            ActivityItemId: activity.activityItemId
        };
        let res = await updateSteps(request);
        if (res.error) {
            NotificationProvider.error("Failed to update steps");
        } else {
            NotificationProvider.success("Successfully updated steps");
            setOverwriteConfig(false);
            setHasUnsavedChanges(false);
        }
        await refetchSteps();
    };

    return (
        <div className={classes.optionsContainer}>
            <SortableContainer onSortEnd={onSortEnd} useDragHandle>
                {values.map((v, index) => (
                    <SortableItem
                        key={v.id || `step-${index}`}
                        index={index}
                        option={v}
                        rowIndex={index}
                        onValueChange={onValueChange}
                        onDeleteValue={onDeleteValue}
                        onSelectStep={onSelectStep}
                        vendorLocked={vendorLocked}
                    />
                ))}
            </SortableContainer>

            <Authorize profile={activityPermissionProfile.WORK_ORDER_PAGE_SAVE}>
                <Button variant="text" onClick={onAddNewStep} className={classes.addStepBtn} disabled={vendorLocked}>
                    + Add a new step
                </Button>
            </Authorize>

            <div className={classes.saveRow}>
                <Authorize profile={activityPermissionProfile.WORK_ORDER_PAGE_SAVE}>
                    {!vendorLocked && <>
                        <Button className={classes.saveBtn} onClick={onSaveAction} color='primary' variant="outlined" disabled={updatingSteps}>
                            {updatingSteps ? 'Saving...' : 'Save'}
                        </Button>
                        <Tooltip title="Update the Steps to Complete of this Activity for future use.">
                            <FormControlLabel
                                control={<Checkbox
                                    onChange={(e, value) => setOverwriteConfig(value)}
                                    checked={overwriteConfig}
                                />}
                                className={classes.overwriteConfig}
                                label='Overwrite Configuration'
                            />
                        </Tooltip>
                    </>}
                </Authorize>
            </div>
        </div>
    );
};

export default StepsToComplete;


const SortableItem = sortableElement(({ option, rowIndex, onValueChange, onDeleteValue, onSelectStep, vendorLocked }) => {
    const classes = StepsToCompleteStyles();
    const DragHandle = sortableHandle(() => (
        <span className={classes.dragContainer}>
            <DragIndicatorIcon className={classes.dragIcon} color="secondary" />
        </span>
    ));

    return (
        <Grid item container alignItems="center" className={classes.multiValueOpt}>
            <Grid item className={classes.stepRow}>
                <Authorize profile={activityPermissionProfile.WORK_ORDER_PAGE_SAVE}>
                    {!vendorLocked && <DragHandle />}
                </Authorize>
                <FormControlLabel
                    control={
                        <Authorize profile={activityPermissionProfile.WORK_ORDER_PAGE_FIELDS}>
                            <Checkbox
                                onChange={(e, value) => onSelectStep(rowIndex, value)}
                                checked={option.isCompleted}
                                size="small"
                                disabled={vendorLocked}
                            />
                        </Authorize>
                    }
                    className={classes.checkbox}
                />
                <Authorize profile={activityPermissionProfile.WORK_ORDER_PAGE_FIELDS}>
                    <TextField
                        value={option?.description ?? ""}
                        variant="standard"
                        autoFocus={!option?.description}
                        onChange={e => onValueChange(e.target.value, rowIndex)}
                        className={classes.stepInput}
                        disabled={vendorLocked}
                        inputProps={{
                            className: option.isCompleted ? classes.completedStep : undefined,
                        }}
                    />
                </Authorize>
                <Authorize profile={activityPermissionProfile.WORK_ORDER_PAGE_SAVE}>
                    <IconButton onClick={() => onDeleteValue(rowIndex)} disabled={vendorLocked}>
                        <HighlightOffOutlinedIcon className={classes.deleteIcon} />
                    </IconButton>
                </Authorize>
            </Grid>
        </Grid>
    );
});

const SortableContainer = sortableContainer(({ children }) => {
    return <Grid container direction="column">{children}</Grid>;
});
